import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["waistInput", "hipInput", "ratioResult"]

  connect() {
    this.calculateRatio()
    this.toggleVirtualFields()
  }

  toggleVirtualFields(event) {
    const virtualCheckbox = this.element.querySelector('#appointment_virtual_consultation')
    if (!virtualCheckbox) return

    const isVirtual = virtualCheckbox.checked
    const scaleFields = [
      'appointment[tbw_water]',
      'appointment[fat_weight]',
      'appointment[ffm]',
      'appointment[bmr]',
      'appointment[bmi]',
      'appointment[fat_percentage]',
      'appointment[waist_circumference]',
      'appointment[hip_circumference]',
      'appointment[waist_hip_ratio]',
      'appointment[visceral_fat]',
      'appointment[mme]'
    ]

    scaleFields.forEach(fieldName => {
      const field = this.element.querySelector(`input[name="${fieldName}"]`)
      if (field) {
        field.disabled = isVirtual
        if (isVirtual) {
          field.value = ''
        }
      }
    })

    // Recalcular ratio si se deshabilitó
    if (isVirtual) {
      try {
        if (this.hasRatioResultTarget) {
          this.ratioResultTarget.value = ''
        }
      } catch (e) {
        // Si no existe el target, no hacer nada
      }
    }
  }

  calculateRatio() {
    try {
      if (!this.hasWaistInputTarget || !this.hasHipInputTarget || !this.hasRatioResultTarget) {
        return
      }

      // Si los campos están deshabilitados (consulta virtual), no calcular
      if (this.waistInputTarget.disabled || this.hipInputTarget.disabled) {
        if (this.hasRatioResultTarget) {
          this.ratioResultTarget.value = ""
        }
        return
      }

      const waist = parseFloat(this.waistInputTarget.value) || 0
      const hip = parseFloat(this.hipInputTarget.value) || 0
      
      if (waist > 0 && hip > 0) {
        const ratio = waist / hip
        this.ratioResultTarget.value = ratio.toFixed(2)
      } else {
        this.ratioResultTarget.value = ""
      }
    } catch (e) {
      // Si no existen los targets, no hacer nada
    }
  }

  // Método para validar inputs decimales
  validateDecimalInput(event) {
    const input = event.target
    const precision = this.getPrecisionForField(input)
    
    // Guardar la posición del cursor (solo para inputs que lo soporten)
    let cursorPosition = null
    try {
      cursorPosition = input.selectionStart
    } catch (e) {
      // input type="number" no soporta selectionStart
    }
    
    const originalValue = input.value
    
    // Permitir solo números y un punto decimal
    let value = input.value.replace(/[^0-9.]/g, '')
    
    // Evitar múltiples puntos decimales
    let parts = value.split('.')
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('')
      parts = value.split('.')
    }
    
    // Validar parte entera
    if (parts[0] && parts[0].length > precision.integer) {
      parts[0] = parts[0].slice(0, precision.integer)
    }
    
    // Validar parte decimal
    if (parts[1] && parts[1].length > precision.decimal) {
      parts[1] = parts[1].slice(0, precision.decimal)
    }
    
    // Reconstruir el valor
    if (parts.length === 2) {
      value = parts[0] + '.' + parts[1]
    } else {
      value = parts[0]
    }
    
    // Solo actualizar si el valor cambió
    if (value !== originalValue) {
      input.value = value
      
      // Restaurar posición del cursor solo si es posible
      if (cursorPosition !== null) {
        try {
          const lengthDiff = value.length - originalValue.length
          const newCursorPosition = Math.max(0, cursorPosition + lengthDiff)
          input.setSelectionRange(newCursorPosition, newCursorPosition)
        } catch (e) {
          // Si falla, no hacer nada (input type="number")
        }
      }
    }
    
    // Si es waist o hip, recalcular ratio
    if (input === this.waistInputTarget || input === this.hipInputTarget) {
      this.calculateRatio()
    }
  }

  // Obtener precisión según el campo
  getPrecisionForField(input) {
    const fieldName = input.name
    
    // Mapeo de campos a sus precisiones según la DB
    const precisions = {
      // precision(6,2) - 4 dígitos enteros + 2 decimales
      'appointment[height]': { integer: 4, decimal: 2 },
      'appointment[current_weight]': { integer: 4, decimal: 2 },
      'appointment[tbw_water]': { integer: 4, decimal: 2 },
      'appointment[fat_weight]': { integer: 4, decimal: 2 },
      'appointment[ffm]': { integer: 4, decimal: 2 },
      'appointment[waist_circumference]': { integer: 4, decimal: 2 },
      'appointment[hip_circumference]': { integer: 4, decimal: 2 },
      
      // precision(7,2) - 5 dígitos enteros + 2 decimales
      'appointment[bmr]': { integer: 5, decimal: 2 },
      
      // precision(4,2) - 2 dígitos enteros + 2 decimales
      'appointment[bmi]': { integer: 2, decimal: 2 },
      'appointment[fat_percentage]': { integer: 2, decimal: 2 },
      
      // precision(3,2) - 1 dígito entero + 2 decimales
      'appointment[waist_hip_ratio]': { integer: 1, decimal: 2 }
    }
    
    return precisions[fieldName] || { integer: 4, decimal: 2 } // default
  }
}